import { supabase, isSupabaseConfigured } from '../lib/supabase';

/**
 * Plan Service
 * Functions to fetch and manage plan data including workouts, exercises, meals, and progress
 */

/**
 * Check if Supabase is configured before making requests
 */
function checkSupabaseConfig() {
  if (!isSupabaseConfigured) {
    return { success: false, error: 'Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file.', data: [] };
  }
  return null;
}

/**
 * Get full plan details with all modules
 * @param {string} planId - Plan ID
 * @returns {Promise<Object>} Plan with workouts, exercises, meals, resources, checkpoints, and config
 */
export async function getPlanDetails(planId) {
  const configCheck = checkSupabaseConfig();
  if (configCheck) return configCheck;

  console.log('📥 Fetching plan details for:', planId);

  try {
    // Fetch plan with all related data in parallel
    const [planResult, workoutsResult, exercisesResult, mealsResult, resourcesResult, checkpointsResult, configResult] = await Promise.all([
      supabase.from('user_programs').select('*').eq('id', planId).single(),
      supabase.from('plan_workouts').select('*').eq('program_id', planId).order('scheduled_date', { ascending: true }),
      supabase.from('plan_exercises').select('*').or(`program_id.eq.${planId},is_global.eq.true`).order('exercise_name', { ascending: true }),
      supabase.from('plan_meals').select('*').eq('program_id', planId).order('scheduled_date', { ascending: true }),
      supabase.from('plan_resources').select('*').or(`program_id.eq.${planId},is_global.eq.true`).order('display_order', { ascending: true }),
      supabase.from('plan_checkpoints').select('*').eq('program_id', planId).order('target_date', { ascending: true }),
      supabase.from('plan_configurations').select('*').eq('program_id', planId).single(),
    ]);

    if (planResult.error) throw planResult.error;

    const plan = {
      ...planResult.data,
      workouts: workoutsResult.data || [],
      exercises: exercisesResult.data || [],
      meals: mealsResult.data || [],
      resources: resourcesResult.data || [],
      checkpoints: checkpointsResult.data || [],
      configuration: configResult.data || null,
    };

    console.log('✅ Plan details fetched:', plan.program_name);
    return { success: true, data: plan };
  } catch (error) {
    console.error('❌ Error fetching plan details:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get workouts for a plan
 * @param {string} planId - Plan ID
 * @param {Object} dateRange - Optional {startDate, endDate}
 * @returns {Promise<Array>} Array of workouts
 */
export async function getWorkouts(planId, dateRange = {}) {
  const configCheck = checkSupabaseConfig();
  if (configCheck) return configCheck;

  try {
    let query = supabase
      .from('plan_workouts')
      .select('*')
      .eq('program_id', planId);

    if (dateRange.startDate) {
      query = query.gte('scheduled_date', dateRange.startDate);
    }
    if (dateRange.endDate) {
      query = query.lte('scheduled_date', dateRange.endDate);
    }

    query = query.order('scheduled_date', { ascending: true });

    const { data, error } = await query;

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching workouts:', error);
    return { success: false, error: error.message, data: [] };
  }
}

/**
 * Get exercises for a plan
 * @param {string} planId - Plan ID
 * @returns {Promise<Array>} Array of exercises
 */
export async function getExercises(planId) {
  const configCheck = checkSupabaseConfig();
  if (configCheck) return configCheck;

  try {
    const { data, error } = await supabase
      .from('plan_exercises')
      .select('*')
      .or(`program_id.eq.${planId},is_global.eq.true`)
      .order('exercise_name', { ascending: true });

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching exercises:', error);
    return { success: false, error: error.message, data: [] };
  }
}

/**
 * Get meals for a plan
 * @param {string} planId - Plan ID
 * @param {Object} dateRange - Optional {startDate, endDate}
 * @returns {Promise<Array>} Array of meals
 */
export async function getMeals(planId, dateRange = {}) {
  const configCheck = checkSupabaseConfig();
  if (configCheck) return configCheck;

  try {
    let query = supabase
      .from('plan_meals')
      .select('*')
      .eq('program_id', planId);

    if (dateRange.startDate) {
      query = query.gte('scheduled_date', dateRange.startDate);
    }
    if (dateRange.endDate) {
      query = query.lte('scheduled_date', dateRange.endDate);
    }

    query = query.order('scheduled_date', { ascending: true }).order('meal_order', { ascending: true });

    const { data, error } = await query;

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching meals:', error);
    return { success: false, error: error.message, data: [] };
  }
}

/**
 * Get progress data for a plan
 * @param {string} planId - Plan ID
 * @returns {Promise<Object>} Progress summary and entries
 */
export async function getProgress(planId) {
  const configCheck = checkSupabaseConfig();
  if (configCheck) return configCheck;

  try {
    const { data, error } = await supabase
      .from('progress_entries')
      .select('*')
      .eq('program_id', planId)
      .order('entry_date', { ascending: false });

    if (error) throw error;

    // Calculate summary statistics
    const summary = {
      totalEntries: data?.length || 0,
      workouts: data?.filter(e => e.entry_type === 'workout').length || 0,
      measurements: data?.filter(e => e.entry_type === 'measurement').length || 0,
      photos: data?.filter(e => e.entry_type === 'photo').length || 0,
      latestEntry: data?.[0] || null,
    };

    return { success: true, data: { entries: data || [], summary } };
  } catch (error) {
    console.error('Error fetching progress:', error);
    return { success: false, error: error.message, data: { entries: [], summary: {} } };
  }
}

/**
 * Log workout completion
 * @param {string} planId - Plan ID
 * @param {string} workoutId - Workout ID
 * @param {Object} workoutData - Workout completion data
 * @returns {Promise<Object>} Created progress entry
 */
export async function logWorkout(planId, workoutId, workoutData) {
  const configCheck = checkSupabaseConfig();
  if (configCheck) return configCheck;

  try {
    // Get user ID from plan
    const { data: plan, error: planError } = await supabase
      .from('user_programs')
      .select('user_id')
      .eq('id', planId)
      .single();

    if (planError) throw planError;

    const { data, error } = await supabase
      .from('progress_entries')
      .insert({
        user_id: plan.user_id,
        program_id: planId,
        workout_id: workoutId,
        entry_type: 'workout',
        entry_date: workoutData.date || new Date().toISOString().split('T')[0],
        data: workoutData.data || {},
        notes: workoutData.notes || null,
      })
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error logging workout:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Log meal adherence
 * @param {string} planId - Plan ID
 * @param {string} mealId - Meal ID
 * @param {Object} mealData - Meal completion data
 * @returns {Promise<Object>} Created progress entry
 */
export async function logMeal(planId, mealId, mealData) {
  const configCheck = checkSupabaseConfig();
  if (configCheck) return configCheck;

  try {
    // Get user ID from plan
    const { data: plan, error: planError } = await supabase
      .from('user_programs')
      .select('user_id')
      .eq('id', planId)
      .single();

    if (planError) throw planError;

    const { data, error } = await supabase
      .from('progress_entries')
      .insert({
        user_id: plan.user_id,
        program_id: planId,
        entry_type: 'note',
        entry_date: mealData.date || new Date().toISOString().split('T')[0],
        data: {
          meal_id: mealId,
          completed: mealData.completed || false,
          macros: mealData.macros || {},
          ...mealData.data,
        },
        notes: mealData.notes || null,
      })
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error logging meal:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Submit weekly check-in
 * @param {string} planId - Plan ID
 * @param {Object} checkInData - Check-in data
 * @returns {Promise<Object>} Created progress entry
 */
export async function submitCheckIn(planId, checkInData) {
  const configCheck = checkSupabaseConfig();
  if (configCheck) return configCheck;

  try {
    // Get user ID from plan
    const { data: plan, error: planError } = await supabase
      .from('user_programs')
      .select('user_id')
      .eq('id', planId)
      .single();

    if (planError) throw planError;

    const { data, error } = await supabase
      .from('progress_entries')
      .insert({
        user_id: plan.user_id,
        program_id: planId,
        entry_type: 'note',
        entry_date: checkInData.date || new Date().toISOString().split('T')[0],
        data: {
          check_in: true,
          mood: checkInData.mood,
          energy: checkInData.energy,
          adherence: checkInData.adherence,
          ...checkInData.data,
        },
        notes: checkInData.notes || null,
      })
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error submitting check-in:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get workout details with exercises
 * @param {string} workoutId - Workout ID
 * @returns {Promise<Object>} Workout with exercise details
 */
export async function getWorkoutDetails(workoutId) {
  const configCheck = checkSupabaseConfig();
  if (configCheck) return configCheck;

  try {
    const { data: workout, error: workoutError } = await supabase
      .from('plan_workouts')
      .select('*')
      .eq('id', workoutId)
      .single();

    if (workoutError) throw workoutError;

    // Fetch exercises if exercise_order is populated
    let exercises = [];
    if (workout.exercise_order && workout.exercise_order.length > 0) {
      const { data: exerciseData, error: exerciseError } = await supabase
        .from('plan_exercises')
        .select('*')
        .in('id', workout.exercise_order);

      if (!exerciseError) {
        exercises = exerciseData || [];
        // Sort exercises by the order in exercise_order array
        exercises.sort((a, b) => {
          const indexA = workout.exercise_order.indexOf(a.id);
          const indexB = workout.exercise_order.indexOf(b.id);
          return indexA - indexB;
        });
      }
    }

    return { success: true, data: { ...workout, exercises } };
  } catch (error) {
    console.error('Error fetching workout details:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get exercise details
 * @param {string} exerciseId - Exercise ID
 * @returns {Promise<Object>} Exercise details
 */
export async function getExerciseDetails(exerciseId) {
  const configCheck = checkSupabaseConfig();
  if (configCheck) return configCheck;

  try {
    const { data, error } = await supabase
      .from('plan_exercises')
      .select('*')
      .eq('id', exerciseId)
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching exercise details:', error);
    return { success: false, error: error.message };
  }
}

export default {
  getPlanDetails,
  getWorkouts,
  getExercises,
  getMeals,
  getProgress,
  logWorkout,
  logMeal,
  submitCheckIn,
  getWorkoutDetails,
  getExerciseDetails,
};
